import {IcnArrowRightUp, IcnInfoFilled, IcnSwitchHorizontal} from "../../assets/icons";

import * as Yup from 'yup'
import {Form, Formik} from "formik";
import Tippy from "@tippyjs/react";

import {DummyCurrencyOptions, HomeDrawCreditFormOffRampValues} from "../../utils";
import {switchToken} from "../../functions/SwitchToken";

import ModalFormWrapper from "../common/ModalFormWrapper";
import FormFieldset from "../form-elements/FormFieldset";
import FormInputNumber from "../form-elements/FormInputNumber";
import FormInputSelect from "../form-elements/FormInputSelect";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import BlockNumber from "../common/BlockNumber";
import BlockTokenPair from "../common/BlockTokenPair";
import Button from "../common/Button";
import {AvailableBalance} from "../../utils/creditService";


interface HomeDrawCreditOffRampStep0Props {
  data: HomeDrawCreditFormOffRampValues;
  next: (values: HomeDrawCreditFormOffRampValues) => void;
  handleBack: () => void;
  onCloseModal: () => void;
  creditLine: AvailableBalance,
}

const HomeDrawCreditOffRampStep0Schema = Yup.object().shape({
  amount: Yup.number().required('Please enter an amount to proceed.'),
  fiatCurrency: Yup.string().required("Please select currency to proceed."),
  tokens: Yup.array().of(Yup.string()).notRequired()
})

function HomeDrawCreditOffRampStep0(
  {
    data,
    next,
    handleBack,
    onCloseModal,
    creditLine,
  }:HomeDrawCreditOffRampStep0Props) {

  const USERCREDITAMOUNT = creditLine.balance;
  const FIATCURRENCY = data.fiatCurrency.toUpperCase();
  const TOKENCURRENCY = data.tokens[0];

  return (
    <Formik
      initialValues={data}
      validationSchema={HomeDrawCreditOffRampStep0Schema}
      onSubmit={(values) => {
        next(values)
      }}
    >
      {({ isSubmitting, values, errors, touched }) => (
        <Form
          className="c-form"
          noValidate
        >

          <ModalFormWrapper
            headerTitle="Off-ramp to Fiat currency"
            onClick={handleBack}
            onCloseModal={onCloseModal}
          >
            <FormFieldset
              extraClassName="amount"
              legendText="Select amount and currency"
            >

              <FormInputNumber
                extraClassName="boxed"
                formGroupStyle="block-line"
                name="amount"
                labelText="Withdraw from credit"
                placeholder={`0.00 ${TOKENCURRENCY? switchToken(TOKENCURRENCY, "name") : ""}`}
                inputGroupText="$ "
                inputGroupPlacement="left"
                hideError
                decimalScale={2}
                fixedDecimalScale
                suffix={TOKENCURRENCY? ` ${switchToken(TOKENCURRENCY, "name")}` : ""}
                formGroupExtraContent={
                  <BlockTokenPair
                    tokens={values.tokens}
                    tokenName={TOKENCURRENCY}
                  />
                }
              />

              {errors.amount && touched.amount && (
                <FormGroupErrorText text={errors.amount}/>
              )}

              {errors.fiatCurrency && touched.fiatCurrency && (
                <FormGroupErrorText text={errors.fiatCurrency}/>
              )}

              <FormInputNumber
                formGroupStyle="block-line"
                name="credit_balance"
                labelText="Recipient receives"
                // tODO dynamic 5% fee
                value={Number(values.amount) * 0.995}
                inputGroupText="$ "
                inputGroupPlacement="left"
                suffix={values.fiatCurrency? ` ${FIATCURRENCY}` : ""}
                disabled
                readOnly
                decimalScale={2}
                fixedDecimalScale
                formGroupExtraContent={
                  <FormInputSelect
                    name="fiatCurrency"
                    labelText="Select currency"
                    labelIsHidden
                    options={DummyCurrencyOptions}
                    hideError
                    isDisabled
                    read-only
                  />
                }
              />

            </FormFieldset>

            <FormFieldset legendText="Details">

              <ul className="c-form-fieldset-detail-list">

                <li className="exchange-rate">
                  <em>Exchange rate</em>
                  <span>
                    <i><IcnSwitchHorizontal/></i>
                    <span>1 {FIATCURRENCY} = 1 {TOKENCURRENCY} <em>($1.00)</em></span>
                  </span>
                </li>

                <li>
                  <em>
                    Estimated fees
                    <Tippy
                      placement="auto"
                      content="Lorem ipsum dolor sit amet, consectetur adipisicing elit. Autem expedita facilis incidunt inventore maiores modi nemo nisi quaerat reprehenderit sint. Harum impedit in incidunt mollitia quod rem unde? Consequatur, est!"
                      className="c-tooltip"
                    >
                      <i className="c-tooltip-icon"><IcnInfoFilled/></i>
                    </Tippy>
                  </em>

                  <BlockNumber
                    value={Number(values.amount) * 0.005}
                    prefix="$"
                    suffix={` ${TOKENCURRENCY}`}
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </li>

                <li className="is-bordered">
                  <em>Available credit</em>
                  <BlockNumber
                    value={USERCREDITAMOUNT}
                    prefix="$"
                    suffix={` ${TOKENCURRENCY}`}
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </li>

                <li>
                  <em>Draw credit</em>
                  <BlockNumber
                    allowNegative={false}
                    value={values.amount|| 0}
                    prefix="-$"
                    suffix={` ${TOKENCURRENCY}`}
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </li>

                <li className="is-bordered dashed">
                  <em>Updated available credit</em>
                  <BlockNumber
                    value={values.amount ? USERCREDITAMOUNT - Number(values.amount) : USERCREDITAMOUNT}
                    prefix="$"
                    suffix={` ${TOKENCURRENCY}`}
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </li>

              </ul>

            </FormFieldset>

            <div className="c-modal-footer">
              <Button
                extraClassName="has-space-between"
                type="submit"
                text="Complete transaction"
                btnSize="lg"
                icon={<IcnArrowRightUp/>}
                disabled={isSubmitting}
              />
            </div>

          </ModalFormWrapper>

        </Form>
      )}

    </Formik>
  );
}

export default HomeDrawCreditOffRampStep0;